import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, resolveImg } from "@/lib/api";
import { UploadSimple, Trash, Plus, FloppyDisk } from "@phosphor-icons/react";

const inp = "w-full bg-vm-muted border border-vm-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-vm-gold/50 text-vm-fg";
const lbl = "text-xs uppercase tracking-widest text-vm-mutedFg block mb-1.5";

function ImagePicker({ value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Foto enviada.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha no upload.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      {value ? (
        <img src={resolveImg(value)} alt="preview" className="h-16 w-24 rounded-lg object-cover bg-vm-muted border border-vm-border shrink-0" data-testid={`${testid}-preview`} />
      ) : (
        <div className="h-16 w-24 rounded-lg bg-vm-muted border border-vm-border flex items-center justify-center text-vm-mutedFg text-[10px] shrink-0">sem foto</div>
      )}
      <div className="flex-1 min-w-0 space-y-2">
        <label className="inline-flex items-center gap-2 cursor-pointer border border-vm-border rounded-full px-4 py-2 text-xs hover:border-vm-gold/50 transition-colors">
          <UploadSimple size={14} /> {uploading ? "Enviando..." : "Enviar foto"}
          <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testid}-upload`} />
        </label>
        <input className={inp} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="ou cole uma URL de imagem" data-testid={`${testid}-url`} />
      </div>
    </div>
  );
}

function Section({ title, desc, children, testid }) {
  return (
    <div className="rounded-2xl border border-vm-border bg-vm-surface p-5 sm:p-6" data-testid={testid}>
      <h3 className="font-serif text-xl mb-1">{title}</h3>
      {desc && <p className="text-xs text-vm-mutedFg mb-5">{desc}</p>}
      <div className="space-y-5">{children}</div>
    </div>
  );
}

export function MediaManager() {
  const [media, setMedia] = useState(null);
  const [unidades, setUnidades] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [m, u] = await Promise.all([api.get("/content/media"), api.get("/admin/unidades")]);
      setMedia(m.data);
      setUnidades(u.data);
    } catch {
      toast.error("Erro ao carregar mídias.");
    }
  };
  useEffect(() => { load(); }, []);

  if (!media) return <p className="text-vm-mutedFg text-sm">Carregando...</p>;

  const set = (k, v) => setMedia((m) => ({ ...m, [k]: v }));
  const setSlide = (k, i, field, v) =>
    setMedia((m) => ({ ...m, [k]: m[k].map((s, idx) => (idx === i ? { ...s, [field]: v } : s)) }));
  const addSlide = (k, tpl) => setMedia((m) => ({ ...m, [k]: [...(m[k] || []), tpl] }));
  const removeSlide = (k, i) => setMedia((m) => ({ ...m, [k]: m[k].filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content/media", media);
      // Propaga fotos vinculadas às unidades
      for (const o of media.oportunidades || []) {
        if (o.unidade_id && o.image) {
          await api.put(`/admin/unidades/${o.unidade_id}/photo`, { image: o.image }).catch(() => {});
        }
      }
      toast.success("Mídias e conteúdos atualizados no site.");
    } catch {
      toast.error("Erro ao salvar mídias.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h2 className="font-serif text-2xl">Mídias & Conteúdo do Site</h2>
          <p className="text-xs text-vm-mutedFg mt-1">Gerencie todas as fotos e frases exibidas nas páginas públicas. As alterações aparecem no site ao salvar.</p>
        </div>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-vm-gold text-vm-bg font-semibold rounded-full px-6 py-2.5 text-sm disabled:opacity-60" data-testid="save-media-btn">
          <FloppyDisk size={16} weight="bold" /> {saving ? "Salvando..." : "Salvar Tudo"}
        </button>
      </div>

      <div className="grid gap-6">
        {/* HOME */}
        <Section title="Home — Carrossel Principal" desc="Fotos do carrossel da página inicial. Cada foto pode ter uma frase de impacto (título) e um subtítulo exibidos sobre a imagem. Deixe em branco para não exibir texto." testid="media-home">
          <div>
            <label className={lbl}>Título principal da Home (use *asteriscos* para destacar em dourado)</label>
            <input className={inp} value={media.home_hero_title || ""} onChange={(e) => set("home_hero_title", e.target.value)} data-testid="media-hero-title" />
          </div>
          {(media.home_carousel || []).map((s, i) => (
            <div key={i} className="rounded-xl border border-vm-border p-4 space-y-3" data-testid={`carousel-slide-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-vm-gold">Foto {i + 1}</span>
                <button onClick={() => removeSlide("home_carousel", i)} className="p-1.5 text-vm-mutedFg hover:text-red-400" data-testid={`carousel-remove-${i}`}><Trash size={15} /></button>
              </div>
              <ImagePicker value={s.image} onChange={(v) => setSlide("home_carousel", i, "image", v)} testid={`carousel-img-${i}`} />
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Frase de impacto</label>
                  <input className={inp} value={s.title || ""} onChange={(e) => setSlide("home_carousel", i, "title", e.target.value)} placeholder="Ex: Gastronomia de alto padrão" data-testid={`carousel-title-${i}`} />
                </div>
                <div>
                  <label className={lbl}>Subtítulo</label>
                  <input className={inp} value={s.subtitle || ""} onChange={(e) => setSlide("home_carousel", i, "subtitle", e.target.value)} placeholder="Ex: Kaven Restaurante no pátio central" data-testid={`carousel-subtitle-${i}`} />
                </div>
              </div>
            </div>
          ))}
          <button onClick={() => addSlide("home_carousel", { image: "", title: "", subtitle: "" })} className="inline-flex items-center gap-2 border border-vm-border rounded-full px-4 py-2 text-sm hover:border-vm-gold/50 transition-colors" data-testid="carousel-add">
            <Plus size={15} weight="bold" /> Adicionar foto ao carrossel
          </button>
        </Section>

        {/* BANNERS */}
        <Section title="Banners das Páginas" desc="Foto de topo, título e subtítulo de cada página do site." testid="media-banners">
          {[
            { key: "banner_shopping", nome: "O Shopping (/shopping)" },
            { key: "banner_lojas", nome: "Lojas & Espaços (/lojas)" },
            { key: "banner_corporate", nome: "VM Corporate (/corporate)" },
          ].map((b) => (
            <div key={b.key} className="rounded-xl border border-vm-border p-4 space-y-3" data-testid={`banner-${b.key}`}>
              <span className="text-xs uppercase tracking-widest text-vm-gold">{b.nome}</span>
              <ImagePicker value={media[b.key]?.image} onChange={(v) => set(b.key, { ...media[b.key], image: v })} testid={`${b.key}-img`} />
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Título</label>
                  <input className={inp} value={media[b.key]?.title || ""} onChange={(e) => set(b.key, { ...media[b.key], title: e.target.value })} data-testid={`${b.key}-title`} />
                </div>
                <div>
                  <label className={lbl}>Subtítulo</label>
                  <input className={inp} value={media[b.key]?.subtitle || ""} onChange={(e) => set(b.key, { ...media[b.key], subtitle: e.target.value })} data-testid={`${b.key}-subtitle`} />
                </div>
              </div>
            </div>
          ))}
        </Section>

        {/* SHOPPING GALLERY */}
        <Section title="O Shopping — Galeria de Estrutura" desc="Fotos da estrutura física: pátio, fachada, corredores, segurança e conveniências. Edite a legenda de cada foto." testid="media-shopping-gallery">
          {(media.shopping_gallery || []).map((g, i) => (
            <div key={i} className="rounded-xl border border-vm-border p-4 space-y-3" data-testid={`shopping-photo-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-vm-gold">Foto {i + 1}</span>
                <button onClick={() => removeSlide("shopping_gallery", i)} className="p-1.5 text-vm-mutedFg hover:text-red-400" data-testid={`shopping-remove-${i}`}><Trash size={15} /></button>
              </div>
              <ImagePicker value={g.image} onChange={(v) => setSlide("shopping_gallery", i, "image", v)} testid={`shopping-img-${i}`} />
              <div>
                <label className={lbl}>Legenda</label>
                <input className={inp} value={g.label || ""} onChange={(e) => setSlide("shopping_gallery", i, "label", e.target.value)} placeholder="Ex: Corredores amplos e cobertos" data-testid={`shopping-label-${i}`} />
              </div>
            </div>
          ))}
          <button onClick={() => addSlide("shopping_gallery", { image: "", label: "" })} className="inline-flex items-center gap-2 border border-vm-border rounded-full px-4 py-2 text-sm hover:border-vm-gold/50 transition-colors" data-testid="shopping-add">
            <Plus size={15} weight="bold" /> Adicionar foto
          </button>
        </Section>

        {/* OPORTUNIDADES */}
        <Section title="Lojas & Espaços — Oportunidades Prontas" desc="Cards comerciais de destaque (ex: Loja 09, Sobreloja 07). A foto enviada aqui também é vinculada automaticamente à unidade selecionada no inventário." testid="media-oportunidades">
          {(media.oportunidades || []).map((o, i) => (
            <div key={i} className="rounded-xl border border-vm-border p-4 space-y-3" data-testid={`oportunidade-card-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-vm-gold">Oportunidade {i + 1}</span>
                <button onClick={() => removeSlide("oportunidades", i)} className="p-1.5 text-vm-mutedFg hover:text-red-400" data-testid={`oportunidade-remove-${i}`}><Trash size={15} /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Nome do card</label>
                  <input className={inp} value={o.nome || ""} onChange={(e) => setSlide("oportunidades", i, "nome", e.target.value)} placeholder="Ex: Loja 09" data-testid={`oportunidade-nome-${i}`} />
                </div>
                <div>
                  <label className={lbl}>Unidade vinculada (recebe a foto)</label>
                  <select className={inp} value={o.unidade_id || ""} onChange={(e) => setSlide("oportunidades", i, "unidade_id", e.target.value)} data-testid={`oportunidade-unidade-${i}`}>
                    <option value="">— Nenhuma —</option>
                    {unidades.map((u) => <option key={u.id} value={u.id}>{u.nome_unidade} · {u.status}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Valor exibido</label>
                  <input className={inp} value={o.valor || ""} onChange={(e) => setSlide("oportunidades", i, "valor", e.target.value)} placeholder="Ex: R$ 1.800" data-testid={`oportunidade-valor-${i}`} />
                </div>
                <div>
                  <label className={lbl}>Tag / segmento</label>
                  <input className={inp} value={o.tag || ""} onChange={(e) => setSlide("oportunidades", i, "tag", e.target.value)} placeholder="Ex: Confeitaria · Cafeteria" data-testid={`oportunidade-tag-${i}`} />
                </div>
              </div>
              <div>
                <label className={lbl}>Frase de destaque (selo)</label>
                <input className={inp} value={o.destaque || ""} onChange={(e) => setSlide("oportunidades", i, "destaque", e.target.value)} placeholder="Ex: Balcão refrigerado instalado" data-testid={`oportunidade-destaque-${i}`} />
              </div>
              <div>
                <label className={lbl}>Descrição comercial</label>
                <textarea rows={3} className={`${inp} resize-none`} value={o.descricao || ""} onChange={(e) => setSlide("oportunidades", i, "descricao", e.target.value)} data-testid={`oportunidade-descricao-${i}`} />
              </div>
              <div>
                <label className={lbl}>Mensagem do WhatsApp (pré-preenchida)</label>
                <textarea rows={2} className={`${inp} resize-none`} value={o.wa || ""} onChange={(e) => setSlide("oportunidades", i, "wa", e.target.value)} data-testid={`oportunidade-wa-${i}`} />
              </div>
              <div>
                <label className={lbl}>Foto real da unidade</label>
                <ImagePicker value={o.image} onChange={(v) => setSlide("oportunidades", i, "image", v)} testid={`oportunidade-img-${i}`} />
              </div>
            </div>
          ))}
          <button onClick={() => addSlide("oportunidades", { nome: "", unidade_id: "", valor: "", tag: "", destaque: "", descricao: "", wa: "", image: "" })} className="inline-flex items-center gap-2 border border-vm-border rounded-full px-4 py-2 text-sm hover:border-vm-gold/50 transition-colors" data-testid="oportunidade-add">
            <Plus size={15} weight="bold" /> Adicionar oportunidade
          </button>
        </Section>

        {/* CORPORATE */}
        <Section title="VM Corporate — Galeria e Destaque" desc="Fotos reais das salas executivas, consultório, sala de reuniões e coworking, com legendas editáveis." testid="media-corporate">
          <div>
            <label className={lbl}>Texto de introdução da seção</label>
            <textarea rows={3} className={`${inp} resize-none`} value={media.corporate_intro_text || ""} onChange={(e) => set("corporate_intro_text", e.target.value)} data-testid="corporate-intro" />
          </div>
          <div>
            <label className={lbl}>Foto em destaque (ao lado do texto)</label>
            <ImagePicker value={media.corporate_feature_image} onChange={(v) => set("corporate_feature_image", v)} testid="corporate-feature-img" />
          </div>
          {(media.corporate_gallery || []).map((g, i) => (
            <div key={i} className="rounded-xl border border-vm-border p-4 space-y-3" data-testid={`corporate-photo-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-vm-gold">Foto {i + 1}</span>
                <button onClick={() => removeSlide("corporate_gallery", i)} className="p-1.5 text-vm-mutedFg hover:text-red-400" data-testid={`corporate-remove-${i}`}><Trash size={15} /></button>
              </div>
              <ImagePicker value={g.image} onChange={(v) => setSlide("corporate_gallery", i, "image", v)} testid={`corporate-img-${i}`} />
              <div>
                <label className={lbl}>Legenda</label>
                <input className={inp} value={g.label || ""} onChange={(e) => setSlide("corporate_gallery", i, "label", e.target.value)} placeholder="Ex: Sala de reuniões" data-testid={`corporate-label-${i}`} />
              </div>
            </div>
          ))}
          <button onClick={() => addSlide("corporate_gallery", { image: "", label: "" })} className="inline-flex items-center gap-2 border border-vm-border rounded-full px-4 py-2 text-sm hover:border-vm-gold/50 transition-colors" data-testid="corporate-add">
            <Plus size={15} weight="bold" /> Adicionar foto
          </button>
        </Section>

        <div className="flex justify-end">
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-vm-gold text-vm-bg font-semibold rounded-full px-6 py-2.5 text-sm disabled:opacity-60" data-testid="save-media-btn-bottom">
            <FloppyDisk size={16} weight="bold" /> {saving ? "Salvando..." : "Salvar Tudo"}
          </button>
        </div>
      </div>
    </div>
  );
}
